/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Spinner, AutocompleteChips, LinkButton } from '../../../shared/components';
import {
  OperationTaskForm,
  OperationTaskDelegateForm,
  Delegate,
} from '../../../services/types/OperationTask';
import { customDateStr } from '../../../shared/misc/utils';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import userService from '../../../services/userService';
import reducer, { SearchUserAction } from './UserQueryReducer';
import { User } from '../../../services/types/User';

interface Props {
  handleSubmit: (values: OperationTaskDelegateForm, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  task: OperationTaskForm;
  operationNumber: string;
  delegates: Delegate[] | null;
  removeDelegation: (id: number) => void;
}

const taskFormrSchema = Yup.object().shape({
  owner: Yup.string().required('required'),
});

const TaskDelegateForm = (props: Props) => {
  const { handleSubmit, handleCancel, task, operationNumber, delegates, removeDelegation } = props;
  const { t } = useTranslation();
  const navigateHook = useNavigate();
  const [users, loadingUsers, , dispatch] = useServiceReducer(userService.getUsers, reducer, {
    query: '',
    convergence: false,
    includeGS: false,
  });

  const [, updateState] = useState<Record<string, unknown>>();
  const forceUpdate = useCallback(() => updateState({}), []);

  const handleSearchUsers = (userQuery: string) => {
    const action = {
      type: 'QUERY_USER',
      query: userQuery,
      convergence: false,
    } as SearchUserAction;

    dispatch(action);
  };
  const navigateToTask = () => {
    if (!task.code) {
      return;
    }
    let page = '';

    const code = task.code.toLocaleLowerCase();
    if (code.includes('sr_espf')) {
      page = 'supervision-report-espf';
    } else if (code.startsWith('sr')) {
      page = 'supervision-report';
    } else if (code.includes('initial')) {
      page = 'initial-esrs';
    } else {
      const pageStrLen = code.indexOf('_');
      page = code.substring(0, pageStrLen);
    }

    navigateHook(`/operations/${operationNumber}/${page}`);
  };

  return (
    <Formik
      initialValues={{}}
      validationSchema={taskFormrSchema}
      validateOnMount
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as OperationTaskDelegateForm;
        handleSubmit(payload, resetForm);
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <div className="modal__body">
            <div className="task-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('titleTask')}</label>
                  <p className="form__text--readonly">
                    {task.title}
                    <button type="button" onClick={navigateToTask} className="gi-icon">
                      <FontAwesomeIcon icon="external-link-alt" />
                    </button>
                  </p>
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('descriptionOptional')}</label>
                  <p className="form__text--readonly">{task.description}</p>
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-4">
                  <label className="col-form-label">{t('dueDate')}</label>
                  <p className="form__text--readonly">
                    {task.dueDateStr ? customDateStr(task.dueDateStr) : 'N/A'}
                  </p>
                </div>
                <div className="col-sm-4">
                  <label className="col-form-label">{t('status')}</label>
                  <p className="form__text--readonly">{t(`taskStatus.${task.status}`)}</p>
                </div>
              </div>

              {task.isOwner && (
                <>
                  {task.status === 'FINISHED' && (
                    <div className="form-row">
                      <div className="col-sm-12">
                        <span className="fs12 bold">{t('finishedTaskText')}</span>
                      </div>
                    </div>
                  )}
                  <hr />
                  <div className="form-row">
                    <div className="col-sm-12">
                      <AutocompleteChips
                        labelKey="fullName"
                        htmlId="task-delegate-user-typeahead"
                        text={t('delegateTo')}
                        placeholder="Ex. Marlen Bahringer"
                        onSearch={handleSearchUsers}
                        onChange={(selectedUser: User[]) => {
                          const formattedUsers = selectedUser.map((user) => user.email);
                          formik.setFieldValue('owner', formattedUsers);
                        }}
                        list={users}
                        loading={loadingUsers}
                        render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                      />
                    </div>
                  </div>
                  {delegates && delegates.length > 0 && (
                    <div className="form-row mt-1">
                      <div className="col-sm-12">
                        <label className="col-form-label">{`${t('taskDelegatedTo')}:`}</label>
                        <table className="w-100">
                          <tbody>
                            {delegates.map((x) => (
                              <tr key={`delegates_td${x.id}`}>
                                <td>{x.name}</td>
                                <td className="text-right">
                                  <LinkButton
                                    text={t('removeDelegation')}
                                    noFit
                                    underline
                                    onClick={() => {
                                      forceUpdate();
                                      removeDelegation(x.id);
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <hr />
            <Spinner loading={formik.isSubmitting} />
            <Button
              key="btnCancel"
              className="btn-tertiary"
              text={t('cancel')}
              type="reset"
              disabled={formik.isSubmitting}
            />
            {task.isOwner && (
              <Button
                key="btnSave"
                className="btn-primary"
                text={t('delegateTask')}
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !task.isOwner}
              />
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default TaskDelegateForm;
